/**
 * The organization setup wizard's steps (docs/Decisions.md D63 item 5).
 *
 * The type and the plan come first, because they decide what the rest asks:
 * the features step shows only the features the plan includes, and the
 * profile step appears only for a type that has a compliance profile
 * (nonprofit, investment; D64). Every step up to the furthest one reached can
 * be reopened from the progress list; none past it.
 */
import { SETUP_STEP_IDS, orgTypePolicy } from './orgTypes';

const LABELS = {
  type: 'Type',
  plan: 'Plan',
  branding: 'Branding',
  domain: 'Domain',
  signup: 'Signup',
  features: 'Features',
  profile: 'Profile',
  invite: 'Invite',
};

const PROFILE_LABELS = { nonprofit: 'Nonprofit profile', investment: 'Compliance profile' };

/** The steps this org sees, in order: `[{ id, label }]`. */
export function visibleSteps(org, planFeatures) {
  const policy = orgTypePolicy(org?.orgType);
  const features = planFeatures || [];
  return SETUP_STEP_IDS.filter(id => {
    if (id === 'profile') return Boolean(policy?.profile);
    if (id === 'features') {
      return !planFeatures || features.includes('events') || features.includes('video-hosting');
    }
    return true;
  }).map(id => ({
    id,
    label: id === 'profile' ? PROFILE_LABELS[policy.profile] : LABELS[id],
  }));
}

/** Where a step sits in the full order, so progress survives a change of type or plan. */
export function stepRank(id) {
  return SETUP_STEP_IDS.indexOf(id);
}

/** Whether the progress list may open `id`: any step up to the furthest reached. */
export function isReachable(id, reached) {
  const rank = stepRank(id);
  return rank >= 0 && rank <= Math.max(0, stepRank(reached));
}

export function nextStepId(steps, current) {
  const i = steps.findIndex(s => s.id === current);
  return i >= 0 && i < steps.length - 1 ? steps[i + 1].id : null;
}

export function previousStepId(steps, current) {
  const i = steps.findIndex(s => s.id === current);
  return i > 0 ? steps[i - 1].id : null;
}

/**
 * The step to open: the one in the URL if it is visible and reachable (a
 * return from Stripe, or a link), otherwise the furthest reached, otherwise
 * the first.
 */
export function initialStepId(steps, requested, reached) {
  const visible = id => steps.some(s => s.id === id);
  if (requested && visible(requested) && isReachable(requested, reached)) return requested;
  if (reached && visible(reached)) return reached;
  // The furthest reached step is hidden now (the type or plan changed): the
  // last visible step before it.
  const before = steps.filter(s => isReachable(s.id, reached));
  return before.length ? before[before.length - 1].id : steps[0].id;
}

/** The later of two steps. */
export function furthest(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return stepRank(a) >= stepRank(b) ? a : b;
}
